// src/ryan/components/product-blocks/EstudoAtivoDemoBlock.tsx
// Bloco de demonstração "Estudo Ativo" — label/headline/subheadline, duas
// colunas de pilares (inalteradas desde a rodada anterior), um "palco" único
// com coreografia (substituindo o antigo placeholder estático) e o gráfico
// horizontal do fluxo de estudo, agora sincronizado com essa coreografia.
// Completamente separado do "Método GRAIA" (MetodoDemoBlock.tsx), que
// continua existindo no código sem uso — nenhuma classe/CSS/lógica de lá é
// importada aqui; o "palco" é uma composição NOVA, só inspirada na mesma
// linguagem visual (cabeçalho de mock, grifo, flip mecânico, barra de
// domínio) já validada nos outros blocos desta sessão.
//
// PRINCÍPIO DA COREOGRAFIA: em qualquer instante, só existe UM protagonista
// visual — tudo o resto é contexto quieto. Nenhuma fase começa antes da
// anterior ter tido tempo de ser registrada (pausas de ~1-3s entre fases).
// A transição entre os dois "modos" do palco (Apostila/Flashcard) é
// cross-fade + slide simples (Framer Motion, sem layoutId — essa técnica já
// é usada em ApostilaDemoBlock e fica reservada pra aparecer pouco).

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, MessageSquare } from 'lucide-react';
import {
    DemoPillar, DemoClosing, DemoHeader, DemoPillarsList, DemoClosingPanel,
} from './DemoBlockShared';
import './EstudoAtivoDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface EstudoAtivoColumn {
    title: string;
    pillars: DemoPillar[];
}

export interface EstudoAtivoDemoData {
    label: string;
    headline: string;
    subheadline: string;
    flashcardsColumn: EstudoAtivoColumn;
    apostilasColumn: EstudoAtivoColumn;
    flowSteps: string[];
    flowDescription: string;
    // ── Conteúdo do "palco" animado (ver EstudoAtivoStage mais abaixo) ──
    stageParagraph: string;
    // Precisa ser um substring literal de stageParagraph (mesma lógica de
    // indexOf já usada em MetodoDemoBlock pra dividir antes/depois do trecho).
    stageHighlightText: string;
    stageNoteText: string;
    stageFlashcardFront: string;
    stageFlashcardBack: string;
    stageMasteryBefore: number;
    stageMasteryAfter: number;
    pillars: DemoPillar[];
    closing: DemoClosing;
}

export const defaultEstudoAtivoDemoData = (): EstudoAtivoDemoData => ({
    label: 'Método de estudo',
    headline: 'Um jeito mais inteligente de estudar Medicina.',
    subheadline: 'Apostilas interativas para construir o entendimento. Flashcards com algoritmo inteligente para transformar esse entendimento em memória de longo prazo.',
    flashcardsColumn: {
        title: 'Flashcards Adaptativos',
        // Mesma redação de defaultFlashcardsDemoData() (FlashcardsDemoBlock.tsx) — mantida idêntica de propósito.
        pillars: [
            { id: 'ea-flk-1', icon: 'Layers', title: 'Fila Inteligente', text: 'Cada card recebe prioridade dinâmica de acordo com o seu desempenho.' },
            { id: 'ea-flk-2', icon: 'Zap', title: 'Adaptação Contínua', text: 'Cada resposta reorganiza automaticamente a sequência de estudo, mantendo o foco no que mais precisa da sua atenção.' },
            { id: 'ea-flk-3', icon: 'ArrowRight', title: 'Domínio Progressivo', text: 'Conforme você evolui, o sistema reduz a frequência dos conteúdos dominados e prioriza aquilo que ainda precisa ser fortalecido.' },
        ],
    },
    apostilasColumn: {
        title: 'Apostilas Interativas',
        // Mesma lógica: títulos iguais aos de defaultApostilaDemoData() (pilares 2 e 3 já renomeados antes),
        // textos descritivos encurtados pra caber lado a lado com a coluna de Flashcards.
        pillars: [
            { id: 'ea-apt-1', icon: 'ScrollText', title: 'Grifos e notas persistentes', text: 'Marque trechos e adicione notas — tudo fica salvo automaticamente.' },
            { id: 'ea-apt-2', icon: 'ListChecks', title: 'Quiz integrado', text: 'Perguntas curtas testam seu entendimento durante a leitura.' },
            { id: 'ea-apt-3', icon: 'FileText', title: 'Comentários sincronizados', text: 'Comente direto no conteúdo — fica salvo pra revisar depois.' },
        ],
    },
    flowSteps: ['Aprenda.', 'Entenda.', 'Revise.', 'Lembre.'],
    flowDescription: 'Primeiro você entende o conteúdo durante a leitura. Depois, o algoritmo inteligente organiza automaticamente as revisões para que o conhecimento continue vivo até a prova.',
    stageParagraph: 'A resposta inflamatória aguda é desencadeada pela liberação de histamina pelos mastócitos, provocando vasodilatação e aumento da permeabilidade capilar na região afetada.',
    stageHighlightText: 'vasodilatação e aumento da permeabilidade capilar',
    stageNoteText: 'Rever os mediadores químicos da inflamação antes da prova.',
    stageFlashcardFront: 'O que provoca vasodilatação e aumento da permeabilidade capilar na inflamação aguda?',
    stageFlashcardBack: 'A histamina, liberada pelos mastócitos durante a resposta inflamatória aguda.',
    stageMasteryBefore: 22,
    stageMasteryAfter: 58,
    pillars: [],
    closing: {
        lead: 'Duas ferramentas.',
        emphasis: 'Um único método de estudo ativo.',
        items: [
            'A apostila constrói o entendimento — você lê, grifa e testa o que aprendeu.',
            'O flashcard transforma esse entendimento em memória de longo prazo.',
            'Tudo continua vivo até a prova, sem esforço extra pra organizar sua revisão.',
        ],
    },
});

// ── Componente ───────────────────────────────────────────────────────

const EstudoAtivoDemoBlock: React.FC<{ data: EstudoAtivoDemoData }> = ({ data }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    // ── Estado da coreografia do palco — único bloco de estado, sem
    // component "Inner" separado: o fluxo horizontal (fora do palco em si)
    // também depende de activeStepIndex, então tudo fica no mesmo escopo em
    // vez de duas fontes de verdade cruzando props. ──
    const [stageMode, setStageMode] = useState<'apostila' | 'flashcard'>('apostila');
    const [highlighted, setHighlighted] = useState(false);
    const [noteVisible, setNoteVisible] = useState(false);
    const [flipping, setFlipping] = useState(false);
    const [cardFlipped, setCardFlipped] = useState(false);
    const [cardDim, setCardDim] = useState(false);
    const [masteryRaised, setMasteryRaised] = useState(false);
    const [masteryPulse, setMasteryPulse] = useState(false);
    // Qual passo do fluxo horizontal (Aprenda/Entenda/Revise/Lembre) está em
    // destaque agora — acompanha a narrativa por cor/peso, nunca posição.
    const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
    const [resetting, setResetting] = useState(false);
    const [cycleKey, setCycleKey] = useState(0);

    const shouldReduceMotion = useReducedMotion();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (wrapRef.current) observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    const resetPhases = () => {
        setStageMode('apostila');
        setHighlighted(false);
        setNoteVisible(false);
        setFlipping(false);
        setCardFlipped(false);
        setCardDim(false);
        setMasteryRaised(false);
        setMasteryPulse(false);
        setActiveStepIndex(null);
        setResetting(false);
    };

    useEffect(() => {
        if (!visible) { resetPhases(); return; }

        // prefers-reduced-motion: pula direto pro estado final estático
        // representativo — sem cascata, sem transição de palco, sem loop.
        if (shouldReduceMotion) {
            setStageMode('flashcard');
            setCardFlipped(true);
            setMasteryRaised(true);
            setActiveStepIndex(3);
            return;
        }

        resetPhases();
        const timers: ReturnType<typeof setTimeout>[] = [];
        const FLIP_MS = 700;

        // ── FASE 0 — silêncio inicial (~700ms). Palco em modo Apostila, sem
        // grifo, nada se move. ──
        let cursor = 700;

        // ── FASE 1 — o grifo é o único protagonista. ──
        timers.push(setTimeout(() => {
            setHighlighted(true);
            setActiveStepIndex(0); // "Aprenda."
        }, cursor));
        cursor += 1200; // pausa perceptível — só olhar pro grifo

        // ── FASE 2 — a nota desliza; único elemento em movimento. ──
        timers.push(setTimeout(() => {
            setNoteVisible(true);
            setActiveStepIndex(1); // "Entenda."
        }, cursor));
        cursor += 1000;

        // ── FASE 3 — transição de palco (cross-fade + slide). Nada mais na
        // tela muda enquanto ela roda — o fluxo horizontal permanece como
        // está (ainda em "Entenda") até o próximo timer. ──
        timers.push(setTimeout(() => setStageMode('flashcard'), cursor));
        cursor += 600;

        // ── FASE 4 — card mostra a pergunta; pausa antes do flip. ──
        timers.push(setTimeout(() => setActiveStepIndex(2), cursor)); // "Revise."
        cursor += 1000;

        // ── FASE 5 — flip mecânico, sem bounce. Conteúdo troca no meio do
        // giro (quando o card está "de perfil"), não no início/fim. ──
        timers.push(setTimeout(() => setFlipping(true), cursor));
        timers.push(setTimeout(() => setCardFlipped(true), cursor + FLIP_MS / 2));
        timers.push(setTimeout(() => setFlipping(false), cursor + FLIP_MS));
        cursor += FLIP_MS + 300; // giro + pausa de causalidade antes do domínio

        // ── FASE 6 — domínio sobe como CONSEQUÊNCIA do flip, não em
        // paralelo a ele. Card fica discreto pra barra ser o foco. ──
        timers.push(setTimeout(() => {
            setMasteryRaised(true);
            setMasteryPulse(true);
            setCardDim(true);
            setActiveStepIndex(3); // "Lembre."
        }, cursor));
        cursor += 650;
        timers.push(setTimeout(() => {
            setMasteryPulse(false);
            setCardDim(false);
        }, cursor));

        // ── FASE 7 — resultado parado, pausa longa pra ser lido com calma. ──
        cursor += 2800;

        // ── FASE 8 — reset: fade geral suave, depois reinicia o ciclo. ──
        timers.push(setTimeout(() => setResetting(true), cursor));
        cursor += 500;
        timers.push(setTimeout(() => setCycleKey(k => k + 1), cursor));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, data, cycleKey, shouldReduceMotion]);

    const highlightIdx = data.stageParagraph.indexOf(data.stageHighlightText);
    const paragraphBefore = highlightIdx >= 0 ? data.stageParagraph.slice(0, highlightIdx) : data.stageParagraph;
    const paragraphAfter = highlightIdx >= 0 ? data.stageParagraph.slice(highlightIdx + data.stageHighlightText.length) : '';

    const panelTransition = { duration: shouldReduceMotion ? 0 : 0.55, ease: [0.4, 0, 0.2, 1] as const };

    return (
        <div className="rpb-demo-wrap" ref={wrapRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />

            {/* Duas colunas — flashcards à esquerda, apostilas à direita, cada
                uma com seu próprio DemoPillarsList (layout="column" padrão,
                mesmo componente/CSS que ApostilaDemoBlock e FlashcardsDemoBlock
                já usam ao lado do mock). Inalterado desde a rodada anterior. */}
            <div className={`rpb-ea-columns ${visible ? 'rpb-ea-columns--visible' : ''}`}>
                <div className="rpb-ea-column">
                    <h3 className="rpb-ea-column-title">{data.flashcardsColumn.title}</h3>
                    <DemoPillarsList pillars={data.flashcardsColumn.pillars} visible={visible} />
                </div>
                <div className="rpb-ea-column">
                    <h3 className="rpb-ea-column-title">{data.apostilasColumn.title}</h3>
                    <DemoPillarsList pillars={data.apostilasColumn.pillars} visible={visible} />
                </div>
            </div>

            {/* "Palco" único com dois modos + fluxo horizontal — ambos dentro
                do MESMO wrapper de reset (rpb-ea-dynamic), porque a Fase 8
                ("fade geral") precisa apagar os dois juntos, não só o palco. */}
            <div className={`rpb-ea-stage-wrap ${visible ? 'rpb-ea-stage-wrap--visible' : ''}`}>
                <div className={`rpb-ea-dynamic ${resetting ? 'rpb-ea-dynamic--resetting' : ''}`}>
                    <div className="rpb-ea-stage">
                        <div className="rpb-ea-stage-header">
                            <span className="rpb-ea-stage-logo">Graia Medicina</span>
                            <span className="rpb-ea-stage-sep" />
                            <span className="rpb-ea-stage-subtitle">
                                {stageMode === 'apostila' ? 'Apostilas' : 'Flashcards'}
                            </span>
                        </div>

                        <div className="rpb-ea-stage-body">
                            <AnimatePresence initial={false}>
                                {stageMode === 'apostila' ? (
                                    <motion.div
                                        key="apostila"
                                        className="rpb-ea-stage-panel"
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -24 }}
                                        transition={panelTransition}
                                    >
                                        <p className="rpb-ea-stage-hint">Trecho da apostila</p>
                                        <p className="rpb-ea-stage-paragraph">
                                            {highlightIdx >= 0 ? (
                                                <>
                                                    {paragraphBefore}
                                                    <mark className={`rpb-ea-stage-highlight${highlighted ? ' rpb-ea-stage-highlight--on' : ''}`}>
                                                        {data.stageHighlightText}
                                                    </mark>
                                                    {paragraphAfter}
                                                </>
                                            ) : data.stageParagraph}
                                        </p>
                                        {noteVisible && data.stageNoteText && (
                                            <div className="rpb-ea-stage-note rpb-ea-stage-note--in">
                                                <MessageSquare size={11} />
                                                <span>{data.stageNoteText}</span>
                                            </div>
                                        )}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="flashcard"
                                        className="rpb-ea-stage-panel"
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -24 }}
                                        transition={panelTransition}
                                    >
                                        <div className={`rpb-ea-stage-card${cardDim ? ' rpb-ea-stage-card--dim' : ''}`}>
                                            <div
                                                className={[
                                                    'rpb-ea-stage-card-face',
                                                    flipping ? 'rpb-ea-stage-card-face--flipping' : '',
                                                    cardFlipped ? 'rpb-ea-stage-card-back' : '',
                                                ].filter(Boolean).join(' ')}
                                            >
                                                <p className="rpb-ea-stage-card-hint">{cardFlipped ? 'Resposta' : 'Pergunta'}</p>
                                                <p className="rpb-ea-stage-card-text">
                                                    {cardFlipped ? data.stageFlashcardBack : data.stageFlashcardFront}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="rpb-ea-stage-mastery">
                                            <div className="rpb-ea-stage-mastery-row">
                                                <span className="rpb-ea-stage-mastery-label">Domínio</span>
                                                <span className={`rpb-ea-stage-mastery-value${masteryPulse ? ' rpb-ea-stage-mastery-value--pulse' : ''}`}>
                                                    {masteryRaised ? data.stageMasteryAfter : data.stageMasteryBefore}%
                                                </span>
                                            </div>
                                            <div className="rpb-ea-stage-mastery-track">
                                                <div
                                                    className={`rpb-ea-stage-mastery-fill${masteryPulse ? ' rpb-ea-stage-mastery-fill--pulse' : ''}`}
                                                    style={{ width: `${masteryRaised ? data.stageMasteryAfter : data.stageMasteryBefore}%` }}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Fluxo horizontal (Aprenda → Entenda → Revise → Lembre) —
                        mesmo padrão visual de .rpb-met-steps em
                        MetodoDemoBlock.css (pill com número + texto, seta
                        ArrowRight entre os itens), adaptado ao prefixo
                        rpb-ea-. Sincronizado com activeStepIndex, atualizado
                        pelos MESMOS timers da coreografia acima — nunca uma
                        lógica paralela separada. */}
                    {data.flowSteps.length > 0 && (
                        <div className="rpb-ea-flow">
                            <div className="rpb-ea-flow-steps">
                                {data.flowSteps.map((step, i) => (
                                    <React.Fragment key={i}>
                                        <div className={`rpb-ea-flow-step${activeStepIndex === i ? ' rpb-ea-flow-step--active' : ''}`}>
                                            <span className="rpb-ea-flow-step-num">{i + 1}</span>
                                            <span className="rpb-ea-flow-step-text">{step}</span>
                                        </div>
                                        {i < data.flowSteps.length - 1 && (
                                            <span className="rpb-ea-flow-arrow"><ArrowRight size={13} /></span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            {data.flowDescription && (
                                <p className="rpb-ea-flow-description">{data.flowDescription}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {data.pillars.length > 0 && (
                <div className="rpb-ea-pillars-row">
                    <DemoPillarsList pillars={data.pillars} visible={visible} layout="row" />
                </div>
            )}

            <DemoClosingPanel closing={data.closing} visible={visible} />
        </div>
    );
};

export default EstudoAtivoDemoBlock;
